import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography } from '@mui/material';
import { Icons } from '../../../../config/MuiIcons.config';
import { AppTheme } from '../../../../theme/App.theme';
import wordCompletionGameService from '../../../../services/WordCompletionGameService';
import { useSnackbar } from '../../../../contexts/Snackbar.context';
import { SNACKBAR_VARIANT, APP_MESSAGE } from '../../../../config/Messages.config';
import MUIButton from '../../../../components/MUIButton/MUIButton';
import MUILoader from '../../../../components/MUILoader/MUILoader';
import './WordCompletionGame.style.css';

const STORAGE_KEY = 'wordCompletionGameData';

const WordCompletionGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const { showSnackbar } = useSnackbar();
  const { WORD_COMPLETION_GAME } = APP_MESSAGE;
  const { FETCH } = WORD_COMPLETION_GAME;
  const { GAME_SESSION, GAME_SESSION_COMPLETE } = FETCH;

  // Game state
  const [passage, setPassage] = useState(null);
  const [session, setSession] = useState(null);
  const [words, setWords] = useState([]); // Available words to drag
  const [blanks, setBlanks] = useState([]); // Blank positions with correct answers
  const [completedWords, setCompletedWords] = useState([]); // Words user has filled
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const timerIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Parse passage to extract words in curly braces
  const parsePassage = useCallback((passageText) => {
    if (!passageText) return { parsedText: '', blanks: [], words: [] };

    const blankPattern = /\{([^}]+)\}/g;
    const blanks = [];
    const words = [];
    let match;
    let blankIndex = 0;

    // Extract all words in curly braces first
    const matches = [];
    while ((match = blankPattern.exec(passageText)) !== null) {
      const word = match[1].trim();
      matches.push({
        word,
        index: match.index,
        fullMatch: match[0],
      });
      blanks.push({
        id: `blank-${blankIndex}`,
        correctWord: word,
        userWord: null,
        isCorrect: null,
        position: match.index,
      });
      words.push(word);
      blankIndex++;
    }

    // Replace curly braces with blank placeholders (need to do in reverse order to maintain indices)
    let parsedText = passageText;
    for (let i = matches.length - 1; i >= 0; i--) {
      parsedText = parsedText.substring(0, matches[i].index) + 
                   '[BLANK]' + 
                   parsedText.substring(matches[i].index + matches[i].fullMatch.length);
    }

    // Shuffle words for the word bank
    const shuffledWords = [...words].sort(() => Math.random() - 0.5);

    return {
      parsedText,
      blanks,
      words: shuffledWords,
    };
  }, []);

  // Load game data from localStorage
  const loadGameData = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.sessionId === sessionId) {
          return data;
        }
      }
    } catch (error) {
      console.error('Error loading game data:', error);
    }
    return null;
  }, [sessionId]);

  // Save game data to localStorage
  const saveGameData = useCallback((data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...data,
        sessionId,
        lastUpdated: Date.now(),
      }));
    } catch (error) {
      console.error('Error saving game data:', error);
    }
  }, [sessionId]);

  // Initialize game
  useEffect(() => {
    const initializeGame = async () => {
      try {
        setLoading(true);
        
        // Load from localStorage first
        const storedData = loadGameData();
        
        if (storedData) {
          setPassage(storedData.passage);
          setSession(storedData.session);
          setCompletedWords(storedData.completedWords || []);
          setElapsedTime(storedData.elapsedTime || 0);
          setIsCompleted(storedData.isCompleted || false);
          
          const parsed = parsePassage(storedData.passage?.passage || '');
          const initialBlanks = [...parsed.blanks];
          const initialWords = [...parsed.words];
          
          // Restore words using blankWordMap (primary) or session.completedWords (fallback)
          // Priority 1: Use blankWordMap (has exact blank IDs)
          // Priority 2: Use session.completedWords array and match to blanks
          let blankWordMap = storedData.blankWordMap || {};
          const sessionCompletedWords = storedData.session?.completedWords || [];
          
          // If blankWordMap is empty but we have completedWords from session, build blankWordMap
          if ((!blankWordMap || Object.keys(blankWordMap).length === 0) && 
              sessionCompletedWords.length > 0) {
            // Build blankWordMap by matching completedWords to blanks
            // Match each completed word to the blank where it's the correct answer
            sessionCompletedWords.forEach((word) => {
              // Find the first blank where this word is the correct answer and not already assigned
              const matchingBlank = initialBlanks.find(
                blank => blank.correctWord.toLowerCase() === word.toLowerCase() &&
                         !blankWordMap[blank.id]
              );
              
              if (matchingBlank) {
                blankWordMap[matchingBlank.id] = word;
              }
            });
          }
          
          // Restore words using blankWordMap
          if (blankWordMap && Object.keys(blankWordMap).length > 0) {
            const wordsUsedCount = {}; // Track how many times each CORRECT word is used in blanks
            
            // First pass: restore only CORRECT words to their exact positions
            const updatedBlanks = initialBlanks.map((blank) => {
              const completedWord = blankWordMap[blank.id];
              
              // Only restore if word exists and is CORRECT
              if (completedWord) {
                const isCorrect = completedWord.toLowerCase() === blank.correctWord.toLowerCase();
                
                // Only restore correct words, ignore wrong ones
                if (isCorrect) {
                  // Count how many times this word is used (only for correct words)
                  wordsUsedCount[completedWord] = (wordsUsedCount[completedWord] || 0) + 1;
                  return {
                    ...blank,
                    userWord: completedWord,
                    isCorrect: true,
                  };
                }
              }
              // Return blank without word if it's wrong or doesn't exist
              return {
                ...blank,
                userWord: null,
                isCorrect: null,
              };
            });
            
            // Second pass: remove only CORRECT words from initialWords array
            const remainingWords = [...initialWords];
            Object.keys(wordsUsedCount).forEach(word => {
              const countToRemove = wordsUsedCount[word];
              let removed = 0;
              // Remove exactly the number of times the word is used in blanks (only correct words)
              for (let i = remainingWords.length - 1; i >= 0 && removed < countToRemove; i--) {
                if (remainingWords[i] === word) {
                  remainingWords.splice(i, 1);
                  removed++;
                }
              }
            });
            
            // Update completedWords state with only correct words
            const correctCompletedWords = updatedBlanks
              .filter(b => b.isCorrect === true)
              .map(b => b.userWord)
              .filter(Boolean);
            setCompletedWords(correctCompletedWords);
            
            setBlanks(updatedBlanks);
            setWords(remainingWords);
          } else {
            // No stored data or old format - start fresh
            setBlanks(initialBlanks);
            setWords(initialWords);
            setCompletedWords([]);
          }
        } else {
          // If no stored data, redirect back
          showSnackbar('Game session not found', SNACKBAR_VARIANT.ERROR);
          navigate('/daily-challenge');
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error('Error initializing game:', error);
        showSnackbar('Failed to load game', SNACKBAR_VARIANT.ERROR);
        setLoading(false);
      }
    };

    if (sessionId) {
      initializeGame();
    } else {
      showSnackbar('Invalid game session', SNACKBAR_VARIANT.ERROR);
      navigate('/daily-challenge');
    }
  }, [sessionId, loadGameData, parsePassage, navigate, showSnackbar]);

  // Timer effect
  useEffect(() => {
    if (!loading && !isCompleted && session && passage) {
      // Initialize start time only once
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now() - (elapsedTime * 1000);
      }
      
      timerIntervalRef.current = setInterval(() => {
        const newElapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedTime(newElapsedTime);
        
        // Get current blanks state for saving
        setBlanks(currentBlanks => {
          // Build blankWordMap from current blanks state - only store CORRECT words
          const currentBlankWordMap = {};
          currentBlanks.forEach(blank => {
            // Only store if word is correct
            if (blank.userWord && blank.isCorrect === true) {
              currentBlankWordMap[blank.id] = blank.userWord;
            }
          });
          
          // Save to localStorage every second - only store blankWordMap with correct words
          const currentData = {
            passage,
            session,
            blankWordMap: currentBlankWordMap, // Only store mapping of blankId -> userWord for CORRECT words
            elapsedTime: newElapsedTime,
            isCompleted,
          };
          saveGameData(currentData);
          
          return currentBlanks; // Return unchanged
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [loading, isCompleted, session, passage, elapsedTime, saveGameData]);

  // Check game completion
  useEffect(() => {
    if (blanks.length === 0) return;
    
    const allFilled = blanks.every(blank => blank.userWord !== null);
    const allCorrect = blanks.every(blank => blank.isCorrect === true);
    
    if (allFilled && allCorrect && !isCompleted) {
      setIsCompleted(true);
      // Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      handleGameComplete();
    }
  }, [blanks, isCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Handle drag start
  const handleDragStart = (e, word) => {
    e.dataTransfer.setData('text/plain', word);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop on blank
  const handleDrop = (e, blankId) => {
    e.preventDefault();
    const word = e.dataTransfer.getData('text/plain');
    
    setBlanks(prevBlanks => {
      const blankToUpdate = prevBlanks.find(blank => blank.id === blankId);
      if (!blankToUpdate) return prevBlanks;

      // If blank already has a word, return it to the words array
      const existingWord = blankToUpdate.userWord;
      
      const updatedBlanks = prevBlanks.map(blank => {
        if (blank.id === blankId) {
          const isCorrect = word.toLowerCase() === blank.correctWord.toLowerCase();
          return {
            ...blank,
            userWord: word,
            isCorrect,
          };
        }
        return blank;
      });
      
      // Return existing word to words array if it exists
      if (existingWord) {
        setWords(prevWords => {
          // Check if word already exists in the array to avoid duplicates
          if (!prevWords.includes(existingWord)) {
            return [...prevWords, existingWord];
          }
          return prevWords;
        });
      }
      
      // Store only blankWordMap (mapping of blankId -> userWord)
      // We only store correct words in blankWordMap, wrong words are not stored
      const blankWordMap = {};
      updatedBlanks.forEach(blank => {
        // Only store if word is correct
        if (blank.userWord && blank.isCorrect === true) {
          blankWordMap[blank.id] = blank.userWord;
        }
      });
      const newCompletedWords = updatedBlanks
        .filter(b => b.isCorrect === true)
        .map(b => b.userWord)
        .filter(Boolean);
      setCompletedWords(newCompletedWords);
      
      // Save to localStorage - only store blankWordMap with correct words
      const currentData = {
        passage,
        session,
        blankWordMap, // Only store mapping of blankId -> userWord for CORRECT words
        elapsedTime,
        isCompleted: false,
      };
      saveGameData(currentData);
      
      return updatedBlanks;
    });

    // Remove word from available words
    setWords(prevWords => prevWords.filter(w => w !== word));
  };

  // Handle remove word from blank
  const handleRemoveWord = (blankId) => {
    setBlanks(prevBlanks => {
      const blankToUpdate = prevBlanks.find(blank => blank.id === blankId);
      if (!blankToUpdate || !blankToUpdate.userWord) {
        return prevBlanks;
      }

      const wordToReturn = blankToUpdate.userWord;
      
      // Update blanks first
      const updatedBlanks = prevBlanks.map(blank => {
        if (blank.id === blankId) {
          return {
            ...blank,
            userWord: null,
            isCorrect: null,
          };
        }
        return blank;
      });
      
      // Count how many times this word appears in blanks (both filled and correct word)
      const wordInBlanksCount = prevBlanks.filter(b => 
        b.correctWord === wordToReturn
      ).length;
      
      // Add word back to words array only if we don't exceed the original count
      setWords(prevWords => {
        // Count how many times this word currently exists in the words array
        const currentWordCount = prevWords.filter(w => w === wordToReturn).length;
        // Count how many blanks currently have this word filled (after removal)
        const filledCount = updatedBlanks.filter(b => b.userWord === wordToReturn).length;
        
        // Total should be: wordInBlanksCount = currentWordCount + filledCount
        // If currentWordCount + filledCount < wordInBlanksCount, we can add it back
        if (currentWordCount + filledCount < wordInBlanksCount) {
          return [...prevWords, wordToReturn];
        }
        return prevWords; // Don't add duplicate
      });
      
      const newCompletedWords = updatedBlanks
        .filter(b => b.isCorrect === true)
        .map(b => b.userWord)
        .filter(Boolean);
      setCompletedWords(newCompletedWords);
      
      // Update blankWordMap - only store correct words
      const blankWordMap = {};
      updatedBlanks.forEach(blank => {
        // Only store if word is correct
        if (blank.userWord && blank.isCorrect === true) {
          blankWordMap[blank.id] = blank.userWord;
        }
      });
      
      // Save to localStorage - only store blankWordMap with correct words
      const currentData = {
        passage,
        session,
        blankWordMap, // Only store mapping of blankId -> userWord for CORRECT words
        elapsedTime,
        isCompleted: false,
      };
      saveGameData(currentData);
      
      return updatedBlanks;
    });
  };

  // Handle game completion
  const handleGameComplete = async () => {
    try {
      const totalTime = elapsedTime;
      const completedWordsList = blanks.map(b => b.userWord).filter(Boolean);
      
      const response = await wordCompletionGameService.completeGameSession(sessionId, {
        totalTime,
        completedWords: completedWordsList,
      });

      if (response.statusCode === 200) {
        showSnackbar(GAME_SESSION_COMPLETE.SUCCESS, SNACKBAR_VARIANT.SUCCESS);
        
        // Clear localStorage
        localStorage.removeItem(STORAGE_KEY);
        
        // Navigate back after a delay
        navigate('/daily-challenge');
      } else {
        showSnackbar(GAME_SESSION_COMPLETE.FAILED, SNACKBAR_VARIANT.ERROR);
      }
    } catch (error) {
      console.error('Error completing game:', error);
      showSnackbar(GAME_SESSION_COMPLETE.FAILED, SNACKBAR_VARIANT.ERROR);
    }
  };

  // Handle exit game
  const handleExitGame = async () => {
    try {
      setIsSaving(true);
      
      const response = await wordCompletionGameService.updateUserGameSession(sessionId, {
        elapsedTime,
        completedWords,
        isCompleted: false,
      });

      if (response.statusCode === 200) {
        // Clear localStorage
        localStorage.removeItem(STORAGE_KEY);
        
        // Clear timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
        
        navigate('/daily-challenge');
      } else {
        showSnackbar('Failed to save game progress', SNACKBAR_VARIANT.ERROR);
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Error exiting game:', error);
      showSnackbar('Failed to save game progress', SNACKBAR_VARIANT.ERROR);
      setIsSaving(false);
    }
  };

  // Handle back button click
  const handleBackClick = () => {
    setShowExitModal(true);
  };

  if (loading) {
    return <MUILoader />;
  }

  if (!passage || !session) {
    return (
      <div className="word-completion-error">
        <Typography>Game data not found. Please start a new game.</Typography>
        <MUIButton primary onClick={() => navigate('/daily-challenge')}>
          Go Back
        </MUIButton>
      </div>
    );
  }

  // Render passage with blanks
  const renderPassage = () => {
    if (!passage?.passage) return null;
    
    const parsed = parsePassage(passage.passage);
    const parts = parsed.parsedText.split('[BLANK]');
    
    return (
      <div className="word-completion-passage">
        {parts.map((part, index) => (
          <React.Fragment key={`part-${index}`}>
            <span>{part}</span>
            {index < blanks.length && (
              <span
                className={`word-completion-blank ${blanks[index].isCorrect === true ? 'correct' : blanks[index].isCorrect === false ? 'incorrect' : ''}`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, blanks[index].id)}
              >
                {blanks[index].userWord ? (
                  <span className="word-completion-filled-word">
                    {blanks[index].userWord}
                    {blanks[index].isCorrect !== null && (
                      <span className="word-completion-status-icon">
                        {blanks[index].isCorrect ? (
                          <Icons.CheckCircle style={{ color: '#4CAF50', fontSize: '18px' }} />
                        ) : (
                          <Icons.Error style={{ color: '#F44336', fontSize: '18px' }} />
                        )}
                      </span>
                    )}
                    {!isCompleted && !blanks[index].isCorrect && (
                      <button
                        className="word-completion-remove-btn"
                        onClick={() => handleRemoveWord(blanks[index].id)}
                        type="button"
                      >
                        <Icons.Close style={{ fontSize: '14px' }}/>
                      </button>
                    )}
                  </span>
                ) : (
                  <span className="word-completion-empty-blank">_______</span>
                )}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="word-completion-game">
      {/* Header */}
      <div className="word-completion-header">
        <div className="word-completion-title-row">
          <span className="word-completion-title">
            {passage?.title || "Inspired by Bhagavad Gitaji"}
          </span>
        </div>
        <div className="word-completion-actions-row">
          <button
            className="word-completion-back-btn"
            onClick={handleBackClick}
          >
            <Icons.LeftArrowAngleIcon />{" "}
            <Typography style={{ fontSize: "15px", fontWeight: 500 }}>
              Back
            </Typography>
          </button>
          <div className="word-completion-timer">
            <Icons.TimerIcon />
            <span>{formatTime(elapsedTime)}</span>
          </div>
        </div>
      </div>

      {/* Game Content */}
      <div className="word-completion-content">
        {/* Passage Card */}
        <div className="word-completion-card word-completion-passage-card">
          {renderPassage()}
          {isCompleted && (
            <div className="word-completion-completed-message">
              Passage completed
            </div>
          )}
        </div>

        {/* Words Card */}
        <div className="word-completion-card word-completion-words-card">
          <h3 className="word-completion-words-title">Words</h3>
          <div className="word-completion-words-list">
            {words.length > 0 ? (
              words.map((word, index) => (
                <div
                  key={index}
                  className="word-completion-word-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, word)}
                >
                  {word}
                </div>
              ))
            ) : (
              <div className="word-completion-empty-words">
                Drag words here to remove them
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exit Confirmation Modal */}
      <Dialog
        open={showExitModal}
        onClose={() => !isSaving && setShowExitModal(false)}
        disableEscapeKeyDown={isSaving}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 2 }}>
          <Icons.Warning
            sx={{
              color: AppTheme.PRIMARY_COLOR,
              fontSize: 45,
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1, fontSize: "19px" }}>
            Are you sure you want to exit the game?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your progress will be saved. You can resume later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 2 }}>
          <MUIButton
            primary
            variant="outlined"
            onClick={() => setShowExitModal(false)}
            disabled={isSaving}
          >
            Cancel
          </MUIButton>
          <MUIButton primary onClick={handleExitGame} disabled={isSaving}>
            {isSaving ? "Saving Progress..." : "Exit Game"}
          </MUIButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WordCompletionGame;

